import { AutoFactory, DynamicApplicationThirdPartySettingsApi, OnlinePortalPlaidApi, PlaidCredentialsResponse } from '@sageworks/jpi';
import { defineModule } from 'direct-vuex';
import { PlaidParsedAssetReport } from '@sageworks/jpi/src/auto-generated/model/plaidParsedAssetReport';
import { moduleActionContext } from '@/store/index';
import { createPlaidLink } from '@/utils/plaid-utils';

// eslint-disable-next-line no-use-before-define
const actionContext = (context: any) => moduleActionContext(context, PlaidModule);

export enum PlaidModuleStatus {
	NotStarted = 'NotStarted',
	Loading = 'Loading',
	Success = 'Success',
	Error = 'Error'
}

export interface PlaidModuleState {
	dataAssets: Map<Number, PlaidParsedAssetReport[]>;
	credentials: PlaidCredentialsResponse;
	loadDataAssetsStatus: PlaidModuleStatus;
	addBankAccountsStatus: PlaidModuleStatus;
	documentStatus: PlaidModuleStatus;
	isPlaidEnabled: boolean;
	allowUsersToPullDirectlyFromBankAccounts: boolean | undefined;
}

const PlaidModule = defineModule({
	namespaced: true,

	state: () => {
		return {
			dataAssets: new Map(),
			credentials: {},
			loadDataAssetsStatus: PlaidModuleStatus.NotStarted,
			addBankAccountsStatus: PlaidModuleStatus.NotStarted,
			documentStatus: PlaidModuleStatus.NotStarted,
			isPlaidEnabled: false,
			allowUsersToPullDirectlyFromBankAccounts: undefined
		} as PlaidModuleState;
	},
	mutations: {
		SET_DATA_ASSETS(state, data: Map<Number, PlaidParsedAssetReport[]>) {
			state.dataAssets = data;
		},
		SET_CREDENTIALS(state, data: PlaidCredentialsResponse) {
			state.credentials = data;
		},
		SET_ADD_BANK_ACCOUNTS_STATUS(state, status: PlaidModuleStatus) {
			state.addBankAccountsStatus = status;
		},
		SET_LOAD_DATA_ASSETS_STATUS(state, status: PlaidModuleStatus) {
			state.loadDataAssetsStatus = status;
		},
		SET_DOCUMENT_STATUS(state, status: PlaidModuleStatus) {
			state.documentStatus = status;
		},
		SET_IS_PLAID_ENABLED(state, isEnabled: boolean) {
			state.isPlaidEnabled = isEnabled;
		},
		SET_ALLOW_USERS_TO_PULL_DIRECTLY_FROM_BANK_ACCOUNTS(state, allowUsersToPullDirectlyFromBankAccounts: boolean){
			state.allowUsersToPullDirectlyFromBankAccounts = allowUsersToPullDirectlyFromBankAccounts;
		}
	},
	actions: {
		async loadDataAssets(context, { customerId, applicationId }): Promise<void> {
			const { commit, state } = actionContext(context);
			if (context.state.loadDataAssetsStatus !== PlaidModuleStatus.Loading) {
				try {
					commit.SET_LOAD_DATA_ASSETS_STATUS(PlaidModuleStatus.Loading);
					const plaidService = AutoFactory.get(OnlinePortalPlaidApi);
					const response = await plaidService.getAssetReports(customerId, applicationId);
					const newDataAssets = new Map(state.dataAssets);
					newDataAssets.set(customerId, response?.assetReports ?? []);
					commit.SET_DATA_ASSETS(newDataAssets);
					commit.SET_LOAD_DATA_ASSETS_STATUS(PlaidModuleStatus.Success);
				} catch (error) {
					commit.SET_LOAD_DATA_ASSETS_STATUS(PlaidModuleStatus.Error);
				}
			}
		},
		async addBankAccounts(context, { applicationId, customerId, beforeCreate, created }): Promise<void> {
			const { commit } = actionContext(context);
			try {
				commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.Loading);
				const plaidService = AutoFactory.get(OnlinePortalPlaidApi);
				const response = (await plaidService.getPlaidCredentials(applicationId)) as PlaidCredentialsResponse;
				createPlaidLink(
					response,
					async (public_token: string, metadata: any) => {
						try {
							beforeCreate();
							const mappingId = await plaidService.createAssetReport(applicationId, customerId, `${public_token}|${metadata.institution.name}`);
							created(mappingId);
							commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.Success);
						} catch (error) {
							commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.Error);
						}
					},
					async () => {
						commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.NotStarted);
					}
				);
			} catch (error) {
				commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.Error);
			}
		},
		async resetPlaidStatus(context): Promise<void> {
			const { commit } = actionContext(context);
			commit.SET_LOAD_DATA_ASSETS_STATUS(PlaidModuleStatus.NotStarted);
			commit.SET_ADD_BANK_ACCOUNTS_STATUS(PlaidModuleStatus.NotStarted);
			commit.SET_DOCUMENT_STATUS(PlaidModuleStatus.NotStarted);
		},
		async getPlaidDocumentStatus(context, { customerId, mappingId }): Promise<void> {
			const { commit } = actionContext(context);
			try {
				commit.SET_DOCUMENT_STATUS(PlaidModuleStatus.Loading);
				const plaidService = AutoFactory.get(OnlinePortalPlaidApi);
				const assetReportGenerated = await plaidService.isAssetReportGenerationCompleted(customerId, mappingId);
				if (assetReportGenerated) {
					commit.SET_DOCUMENT_STATUS(PlaidModuleStatus.Success);
				}
			} catch (error) {
				commit.SET_DOCUMENT_STATUS(PlaidModuleStatus.Error);
			}
		},
		async getPlaidEnabled(context): Promise<void> {
			const { commit } = actionContext(context);
			try {
				const plaidService = AutoFactory.get(OnlinePortalPlaidApi);
				const isEnabled = await plaidService.isEnabled();
				commit.SET_IS_PLAID_ENABLED(isEnabled);
			} catch (error) {
				commit.SET_IS_PLAID_ENABLED(false);
			}
		},
		async getAllowUsersToPullDirectlyFromBankAccounts(context, applicationId): Promise<void>{
			const { commit, state } = actionContext(context);
			if (state.allowUsersToPullDirectlyFromBankAccounts !== undefined){
				return;
			}
			try {
				const dyaService = AutoFactory.get(DynamicApplicationThirdPartySettingsApi);
				const allowUsersToPullDirectlyFromBankAccounts = await dyaService.allowUsersToPullPlaidFromBankAccounts(applicationId);
				commit.SET_ALLOW_USERS_TO_PULL_DIRECTLY_FROM_BANK_ACCOUNTS(allowUsersToPullDirectlyFromBankAccounts);
			} catch (error) {
				commit.SET_ALLOW_USERS_TO_PULL_DIRECTLY_FROM_BANK_ACCOUNTS(false);
			}
		}
	},
	getters: {
		selectDataAssets: state => state.dataAssets ?? new Map(),
		selectCredentials: state => state.credentials ?? {},
		selectLoadDataAssetsStatus: state => state.loadDataAssetsStatus,
		selectAddBankAccountsStatus: state => state.addBankAccountsStatus,
		selectDocumentStatus: state => state.documentStatus
	}
});

export default PlaidModule;
